import React from 'react';
import { isAfter, isBefore } from 'date-fns';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Text } from '@eo-locale/react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Calendar } from '@guest-widgets/shared/src/components/Calendar/Calendar';
import { styled, useWidget } from '@guest-widgets/core';

import type { DateRange } from '../../../filters';
import { useSettings } from '../../../settingsContext';

import { CalendarDayPicker, maxAllowedDate, minAllowedDate } from './CalendarDayPicker';

export interface DateRangePickerProps {
  dates?: DateRange;
  onSelect(dates: DateRange): void;
}

interface DateSelected {
  from?: Date;
  to?: Date;
}

export const DateRangePicker = ({ dates, onSelect }: DateRangePickerProps) => {
  const { locale } = useSettings();
  const {
    widgetArea: { basedOnSize },
  } = useWidget();
  const minDate = minAllowedDate(dates?.[0]);
  const maxDate = maxAllowedDate(dates?.[0]);

  const handleRangeSelect = (range: DateSelected | undefined) => {
    // If there is a range already selected, when the user clicks on first selected day a second time,
    // the library returns 'undefined' for both 'from' and 'to' properties: { from: undefined, to: undefined}
    if (!range?.from) {
      return dates ? onSelect([dates[0], dates[0]]) : undefined;
    }
    // If there is a range already selected, when the user clicks on last selected day a second time,
    // the library returns it for 'from' property and 'undefined' for 'to' property: { from: Date, to: undefined}
    const selectedFrom = range.to && isBetween(range.to, dates) ? range.to : range.from;
    const selectedTo = range.to ? range.to : range.from;

    return onSelect([selectedFrom, selectedTo]);
  };

  return (
    <Box sx={{ mt: basedOnSize(2, 2, 6) }}>
      <StyledCalendar
        components={{ Day: CalendarDayPicker }}
        language={locale}
        numberOfMonths={1}
        fromMonth={minDate}
        fromDate={minDate}
        toDate={maxDate}
        toMonth={maxDate}
        mode="range"
        selected={{ from: dates?.[0], to: dates?.[1] }}
        onSelect={handleRangeSelect}
      />
    </Box>
  );
};

const StyledCalendar = styled(Calendar)(
  ({ widgetArea: { basedOnSize }, theme: { typography } }) => ({
    fontFamily: typography.fontFamily,
    ['& .rdp-caption_label']: {
      ...typography.h4,
    },
    ['& .rdp-nav']: {
      top: -16,
    },
    ['& .rdp-day']: {
      borderRadius: 4,
    },
  })
);

const Info = styled(Typography)(({ widgetArea: { basedOnSize }, theme: { spacing } }) => ({
  marginTop: basedOnSize(spacing(8), spacing(8), spacing(4)),
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  '& *:first-child': {
    marginRight: spacing(1),
  },
}));

const isBetween = (selectedDate: Date, dates?: DateRange) =>
  dates?.[0] && dates?.[1] && isAfter(selectedDate, dates[0]) && isBefore(selectedDate, dates[1]);
